import i18n from '../../../../i18n';
import { apiCall } from './api';
import { getBrandLocale } from '../../brandCurrency';

const formatAuditDate = (value) => {
  const locale = getBrandLocale('ar');
  if (!value) return '—';
  return new Date(value).toLocaleString(locale);
};

const mapComplianceCategories = (categories = []) =>
  categories.map((cat) => ({
    id: cat.id,
    title: cat.title,
    description: cat.description || '',
    status: cat.status,
    score: cat.score,
    checks: (cat.checks || []).map((c) => ({
      name: c.check_name || c.name,
      description: c.description || c.check_description || '',
      status: c.status === 'passed' ? 'passed' : c.status === 'warning' ? 'warning' : 'failed',
    })),
  }));

const mapComplianceAuditLog = (logs = []) =>
  logs.map((log) => ({
    action: log.action || log.event_type || log.description || 'حدث',
    user: log.user_name || log.user_id || log.user || 'النظام',
    module: log.module || log.entity_type || log.event_category || 'المحاسبة',
    date: log.timestamp
      ? formatAuditDate(log.timestamp)
      : log.created_at
        ? formatAuditDate(log.created_at)
        : '—',
    status: log.status === 'success' || log.status === 'passed' ? 'success'
      : log.status === 'warning' ? 'warning' : 'error',
  }));

const mapComplianceBackup = (backup) => ({
  lastBackup: backup?.last_backup
    ? (backup.last_backup === 'Never' ? 'لم يتم بعد' : formatAuditDate(backup.last_backup))
    : 'غير متاح',
  nextBackup: backup?.next_backup ? formatAuditDate(backup.next_backup) : 'غير متاح',
  backupSize: backup?.backup_size || 'N/A',
  retention: backup?.retention_period || 'N/A',
  locations: backup?.locations || [],
});

const mapEventLogToAuditEntry = (e) => ({
  action: e.description || e.event_type,
  user_name: e.user_name || e.user_id,
  module: e.event_category || i18n.t('modules.comptabilite.pages.conformiteAuditPage.module_compliance'),
  timestamp: e.event_date && e.event_time ? `${e.event_date}T${e.event_time}` : e.event_date || e.created_at,
  status: e.is_critical ? 'error' : 'success',
});

export const getComplianceOverview = async () => {
  const result = await apiCall('/comptabilite/compliance/overview');
  return result.data;
};

export const getComplianceCheckCategories = async () => {
  const result = await apiCall('/comptabilite/compliance/checks');
  return result.data || [];
};

export const getComplianceAuditLog = async (params = {}) => {
  let endpoint = '/comptabilite/compliance/audit-log';
  const qs = [];
  if (params.limit) qs.push(`limit=${params.limit}`);
  if (params.offset) qs.push(`offset=${params.offset}`);
  if (qs.length) endpoint += `?${qs.join('&')}`;
  const result = await apiCall(endpoint);
  return result.data || [];
};

export const getComplianceBackupStatus = async () => {
  const result = await apiCall('/comptabilite/compliance/backup-status');
  return result.data;
};

export const getComplianceChecks = async () => {
  const [overviewRes, categoriesRes, auditLogRes, backupRes, eventLogRes] = await Promise.all([
    apiCall('/comptabilite/compliance/overview').catch(() => ({ data: null })),
    apiCall('/comptabilite/compliance/checks').catch(() => ({ data: [] })),
    apiCall('/comptabilite/compliance/audit-log?limit=20').catch(() => ({ data: [] })),
    apiCall('/comptabilite/compliance/backup-status').catch(() => ({ data: null })),
    apiCall('/comptabilite/compliance/event-log?limit=20').catch(() => ({ events: [] })),
  ]);

  const overview = overviewRes.data;
  const categories = mapComplianceCategories(categoriesRes.data);
  const eventLogs = eventLogRes.events || eventLogRes.data || [];
  const auditLogs = auditLogRes.data?.length
    ? mapComplianceAuditLog(auditLogRes.data)
    : mapComplianceAuditLog(eventLogs.map(mapEventLogToAuditEntry));

  return {
    overview: {
      complianceScore: overview?.compliance_score ?? 0,
      totalChecks: overview?.total_checks ?? 0,
      passedChecks: overview?.passed_checks ?? 0,
      failedChecks: overview?.failed_checks ?? 0,
      warningChecks: overview?.warning_checks ?? 0,
      pendingChecks: Math.max(0, (overview?.total_checks ?? 0) - (overview?.passed_checks ?? 0) - (overview?.warning_checks ?? 0)),
      lastAudit: overview?.last_audit ?? 'N/A',
      nextAudit: overview?.next_audit ?? 'N/A',
    },
    categories,
    auditLog: auditLogs,
    backup: mapComplianceBackup(backupRes.data),
  };
};

export const updateComplianceCheck = async (id, data) => {
  const payload = { ...data, updated_at: new Date().toISOString() };
  const result = await apiCall(`/comptabilite/compliance/${id}`, 'PUT', payload);
  return result.data;
};

export const transformComplianceCheck = (data) => ({
  id: data.id,
  categoryId: data.category_id,
  categoryTitle: data.category_title,
  categoryTitleAr: data.category_title_ar,
  name: data.check_name,
  description: data.check_description,
  status: data.status,
  score: data.score,
  lastCheckDate: data.last_check_date,
  notes: data.notes,
});
